import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {JbSpringBatchIndicatorService} from 'features/feature-tech/jb/service/jb-spring-batch-indicator.service';
import {CpProvisionRunService} from '../../../service/api/cp-provision-run.service';
import {
    ProvisionRunKpiDto,
    ProvisionRunKpiResponseDto,
    ProvisionTypeKpiDto
} from '../../../model/dto/api/provision-run-kpi.dto';
import {ProvisionContractRunStatus} from '../../../enum/provision-contract-run-status.enum';
import {ProvisionMessageSeverity} from '../../../enum/provision-message-severity.enum';
import {
    ProvisionRunStatusPresentation,
    provisionRunStatusIsAborted,
    provisionRunStatusIsCompleted,
    provisionRunStatusIsFailed,
    provisionRunStatusIsRunning,
    provisionRunStatusPresentation,
    provisionRunStatusProgressPercent
} from '../../../model/provision-run-status-presentation';
import {
    PROVISION_CONTRACT_RUN_STATUS_DISPLAY_ORDER,
    ProvisionContractRunStatusPresentation,
    provisionContractRunStatusPresentation
} from '../../../model/provision-contract-run-status-presentation';
import {
    PROVISION_MESSAGE_SEVERITY_DISPLAY_ORDER,
    ProvisionMessageSeverityPresentation,
    provisionMessageSeverityPresentation
} from '../../../model/provision-message-severity-presentation';

/**
 * Start page of the provision dashboard (default kind '' of route `cp/dashboard`,
 * hash routing `/#/cp/dashboard`) — 04-frontend.md §3, KPI catalog 01 §6 (K-01…K-14).
 *
 * Built following the documented bm original `features/feature-bm/bm/component/protected-components/
 * bm-billing-start-component/bm-billing-start.component.ts` (PAT §J.2): one tile per
 * configuration unit (there BillingRunType, here ProvisionType) with the MOST RECENT run, status
 * chip + progress bar on top, figures below, the whole tile is the link into the run.
 *
 * Data source is ONE backend aggregate: `GET /mvsa/cp/provisionRuns/kpis` via
 * {@link CpProvisionRunService#getKpis} (04 §9.1). The FE does NOT aggregate over raw rows itself
 * (DA-020.7) — which is why there is deliberately no QL widget here, but this custom component. Its
 * alias {@link CpProvisionDashboardStartComponent#KPI_ALIAS} = `cp.dashboard.start.kpi` is set on
 * the root element as `data-widget-alias`, so the e2e playbooks (WP-19) can address it like a
 * widget.
 *
 * TWO hard rules of the presentation:
 *  1. Progress is computed ALONG CP_RUN_STATUS_ORDER, never by enum ordinal
 *     (04 §5.2 / U-09) — encapsulated in {@link provisionRunStatusProgressPercent}. A
 *     *_FAILED status reports the progress of the step it failed in.
 *  2. The risk KPIs K-09…K-12 (liability stock, reserve coverage ratio, agents in
 *     shortfall, agents with a negative provision balance) are shown PROMINENTLY ahead of the plain
 *     run figures (01 §6).
 *
 * SHORTFALL IS NOT THE PAYOUT BRAKE. K-11 counts the run's coverage rows with
 * `shortfallAmount > 0` (reserve below the open liability) — exactly the rows with "Unterdeckung > 0"
 * in the run's "Deckung" tab — and such an agent IS paid out. The payout skips an agent only when
 * his REAL reserve balance is negative (`ProvisionPayoutService.reserveUnderfunded`, guard 4 of the
 * payout, U-03 / M-012). The two figures are therefore separate tiles: "Unterdeckung" (no payout
 * stop) and "Auszahlung gesperrt" (negative reserve, account-wide from the response root).
 *
 * Auto-refresh (04 §4): the component subscribes to `status$` of the job indicator that sits
 * globally in the top bar and reloads the KPIs on the running -> not-running transition. The
 * indicator itself is NEITHER built NOR activated here — that is done by the shell CpDashboardPage.
 */
@Component({
    selector: 'cp-provision-dashboard-start',
    templateUrl: './cp-provision-dashboard-start.component.html',
    styleUrls: ['./cp-provision-dashboard-start.component.scss'],
    standalone: false
})
export class CpProvisionDashboardStartComponent implements OnInit, OnChanges, OnDestroy {

    /** Widget alias of the KPI tiles (04 §3). */
    static readonly KPI_ALIAS: string = 'cp.dashboard.start.kpi';

    busy: boolean;
    initialized: boolean;

    /** Template-visible copy of the alias (static members are not addressable in templates). */
    readonly kpiAlias: string = CpProvisionDashboardStartComponent.KPI_ALIAS;

    /** Display order of the chip rows — explicit, not derived from the ordinal. */
    readonly contractRunStatusOrder: ProvisionContractRunStatus[] = PROVISION_CONTRACT_RUN_STATUS_DISPLAY_ORDER;
    readonly messageSeverityOrder: ProvisionMessageSeverity[] = PROVISION_MESSAGE_SEVERITY_DISPLAY_ORDER;

    /** All provision types delivered by the endpoint — including those without a run (own empty state). */
    provisionTypeKpis: ProvisionTypeKpiDto[] = [];

    /** Timestamp of the server aggregation, for the as-of line below the tiles. */
    generatedAt: number;

    /**
     * Agents the payout brake actually stops: REAL reserve balance below zero (U-03 / M-012,
     * `ProvisionPayoutService.reserveUnderfunded`). Account-wide — the brake is agent-global — so it
     * comes from the response root, not from a tile. `null` when the payload does not carry it.
     */
    agentsWithNegativeReserveBalanceCount: number | null = null;

    kpisLoading: boolean;
    loadFailed: boolean;

    /** Is a cp job currently running? Feeds the hint banner above the tiles. */
    jobRunning: boolean;

    protected jobWasRunning: boolean;
    protected statusSubscription: Subscription;

    constructor(
        protected provisionRunService: CpProvisionRunService,
        protected springBatchIndicatorService: JbSpringBatchIndicatorService,
        protected router: Router) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize component.
     */
    initComponent(): void {
        this.loadKpis();
        this.subscribeToJobCompletion();
        this.initialized = true;
    }

    /**
     * Load the KPI aggregate for every ProvisionType (K-01…K-14).
     */
    loadKpis(): void {
        this.kpisLoading = true;
        this.busy = true;
        this.loadFailed = false;

        this.provisionRunService.getKpis().subscribe({
            next: (response: ProvisionRunKpiResponseDto) => {
                this.provisionTypeKpis = response?.provisionTypes ?? [];
                this.generatedAt = response?.generatedAt;
                this.agentsWithNegativeReserveBalanceCount = response?.agentsWithNegativeReserveBalanceCount ?? null;
                this.kpisLoading = false;
                this.busy = false;
            },
            error: error => {
                console.error('Error loading provision KPIs', error);
                this.provisionTypeKpis = [];
                this.agentsWithNegativeReserveBalanceCount = null;
                this.loadFailed = true;
                this.kpisLoading = false;
                this.busy = false;
            }
        });
    }

    /**
     * Subscribe to the job indicator and reload on the running -> not-running transition (04 §4).
     *
     * The KPI figures are produced BY the cp jobs, so a tile rendered while a job is running is a
     * snapshot of a half-finished run. Reloading on the transition is what makes the dashboard
     * settle on the final figures without the user pressing anything.
     *
     * Deliberately no confirm dialog here (unlike the bm step bar, which asks before refreshing an
     * action surface): a read-only KPI page has nothing to lose by refreshing itself.
     */
    protected subscribeToJobCompletion(): void {
        this.statusSubscription = this.springBatchIndicatorService.status$.subscribe(status => {
            const wasRunning: boolean = this.jobRunning;

            this.jobRunning = status.hasRunningJobs;

            if (status.hasRunningJobs) {
                this.jobWasRunning = true;
            } else if (this.jobWasRunning && wasRunning && !status.hasRunningJobs) {
                this.jobWasRunning = false;
                this.loadKpis();
            }
        });
    }

    /**
     * The whole tile is the link into the run (bm behaviour). No navItem query param on purpose:
     * the shell's subscribeToParams would read it and navigate to that nav item instead, throwing
     * the user back out of the detail. `run` is mapped onto the "Provisionsläufe" nav item by
     * CpDashboardPage.updateActiveNavigationFromRoute.
     */
    navigateToProvisionRun(provisionRunId: number): void {
        this.router.navigate(['/cp/dashboard/run', provisionRunId]);
    }

    /**
     * Way out of the "no provision type configured" empty state (UX8): the config page opened on
     * its `cp.ProvisionType` nav item, the single place a provision type is created.
     */
    navigateToProvisionTypeConfig(): void {
        this.router.navigate(['/cp/config'], {queryParams: {navItem: 'cp.ProvisionType'}});
    }

    /** Has this configured type ever run? A type without a run gets its own empty state. */
    hasRun(typeKpi: ProvisionTypeKpiDto): boolean {
        return !!typeKpi?.latestProvisionRun;
    }

    presentationOf(status: number | undefined): ProvisionRunStatusPresentation {
        return provisionRunStatusPresentation(status);
    }

    /** Progress along CP_RUN_STATUS_ORDER — never along the ordinal (04 §5.2). */
    progressOf(status: number | undefined): number {
        return provisionRunStatusProgressPercent(status);
    }

    isRunning(status: number | undefined): boolean {
        return provisionRunStatusIsRunning(status);
    }

    isCompleted(status: number | undefined): boolean {
        return provisionRunStatusIsCompleted(status);
    }

    isFailed(status: number | undefined): boolean {
        return provisionRunStatusIsFailed(status);
    }

    isAborted(status: number | undefined): boolean {
        return provisionRunStatusIsAborted(status);
    }

    /**
     * Fill colour of the progress bar as a PrimeFlex background class: green when done, red when the
     * run failed, primary otherwise.
     *
     * A class rather than a raw CSS colour on purpose — `[style.background-color]` with a
     * `var(--…)` string is an inline style and bypasses the colour token rules of
     * fe_core_ux-guidelines. The bm reference (bm-billing-start.component.html) binds the colour
     * inline; the palette is identical, only the transport differs.
     */
    progressBackgroundOf(status: number | undefined): string {
        if (this.isFailed(status)) {
            return 'bg-red-500';
        }
        if (this.isCompleted(status)) {
            return 'bg-green-500';
        }
        return 'bg-primary';
    }

    // --- K-02 processed contracts ----------------------------------------------------------------

    contractRunStatusPresentationOf(status: number): ProvisionContractRunStatusPresentation {
        return provisionContractRunStatusPresentation(status);
    }

    contractRunCountOf(kpi: ProvisionRunKpiDto, status: number): number {
        return kpi?.contractRunCountsByStatus?.[status] ?? 0;
    }

    contractRunTotalOf(kpi: ProvisionRunKpiDto): number {
        return this.sumOf(kpi?.contractRunCountsByStatus);
    }

    // --- K-13 open messages -----------------------------------------------------------------------

    messageSeverityPresentationOf(severity: number): ProvisionMessageSeverityPresentation {
        return provisionMessageSeverityPresentation(severity);
    }

    /**
     * K-13 counts BOTH message entities per severity: a payout brake (M-012) lands on the run,
     * a missing contribution (M-001) on the contract run. For "sind Meldungen offen?" the split
     * between the two tables is noise — the run detail (§5.3) has a tab per table for the detail.
     */
    messageCountOf(kpi: ProvisionRunKpiDto, severity: number): number {
        const contractRunMessages: number = kpi?.contractRunMessageCountsBySeverity?.[severity] ?? 0;
        const runMessages: number = kpi?.runMessageCountsBySeverity?.[severity] ?? 0;
        return contractRunMessages + runMessages;
    }

    messageTotalOf(kpi: ProvisionRunKpiDto): number {
        return this.sumOf(kpi?.contractRunMessageCountsBySeverity) + this.sumOf(kpi?.runMessageCountsBySeverity);
    }

    // --- K-14 data gaps ---------------------------------------------------------------------------

    dataGapTotalOf(kpi: ProvisionRunKpiDto): number {
        return (kpi?.contractsWithoutAgentCount ?? 0) + (kpi?.contractsWithoutPriceCount ?? 0);
    }

    // --- K-09…K-12 risk ---------------------------------------------------------------------------

    /**
     * Does this run carry a risk signal at all? Drives the accent of the risk block, so that a clean
     * run does not wear a warning colour just for having a risk section.
     *
     * A reserve coverage quote BELOW 100 % means the reserve does not cover the liability stock. That
     * is a risk signal, not a payout stop — the brake (U-03) only acts on a NEGATIVE reserve.
     */
    hasRiskSignal(kpi: ProvisionRunKpiDto): boolean {
        if (!kpi) {
            return false;
        }
        return (kpi.agentsInShortfallCount ?? 0) > 0
            || (kpi.agentsWithNegativeProvisionBalanceCount ?? 0) > 0
            || (this.agentsWithNegativeReserveBalanceCount ?? 0) > 0
            || (kpi.reserveCoverageQuotePercent != null && kpi.reserveCoverageQuotePercent < 100);
    }

    isCoverageCritical(kpi: ProvisionRunKpiDto): boolean {
        return kpi?.reserveCoverageQuotePercent != null && kpi.reserveCoverageQuotePercent < 100;
    }

    // --- Formatting -----------------------------------------------------------------------------

    formatCurrency(value: number): string {
        return new Intl.NumberFormat('de-DE', {style: 'currency', currency: 'EUR'}).format(value || 0);
    }

    formatNumber(value: number): string {
        return new Intl.NumberFormat('de-DE').format(value || 0);
    }

    /** A count that may be absent: a dash, never a 0 the backend did not report. */
    formatOptionalNumber(value: number | null | undefined): string {
        return value == null ? '–' : this.formatNumber(value);
    }

    /**
     * Percent values are nullable by contract: K-07 is undefined when nothing was activated and
     * K-10 when there is no liability stock. "0 %" would be a claim the backend did not make, so
     * an absent quote is rendered as a dash.
     */
    formatPercent(value: number | null | undefined): string {
        if (value == null) {
            return '–';
        }
        const formatter = new Intl.NumberFormat('de-DE', {minimumFractionDigits: 1, maximumFractionDigits: 1});
        return formatter.format(value) + ' %';
    }

    private sumOf(counts: { [key: number]: number } | undefined): number {
        if (!counts) {
            return 0;
        }
        return Object.values(counts).reduce((total: number, count: number) => total + (count || 0), 0);
    }

    /**
     * Process changes within binding.
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
        this.statusSubscription?.unsubscribe();
    }

}
